import { $, setLoading } from './dom.js';
import { api } from './api.js';
import { trackDirtyForm } from './dirty-form.js';
import { toastError, toastSuccess, confirmAction } from './toast.js';

/**
 * Controller for a resource edited on its **own page**.
 *
 * The four resource modules - reaction menus, ticket panels, embed templates,
 * custom commands - used to edit inside a `<dialog>`. It worked while a form
 * had six fields. It stopped working the moment one of them grew nested
 * repeatable rows: an embed with three embeds and their fields does not fit in
 * a modal, and scrolling inside a scrolling box to reach a save button is the
 * kind of thing people give up on.
 *
 * A page costs one navigation and gives back everything a module page already
 * has: the full width, the floating save bar, a URL you can bookmark or send to
 * a colleague, and a browser Back button that means what it says.
 *
 * The list page keeps its cards; they simply link here.
 */
export interface ResourceEditorOptions {
  /** API segment (`embeds`, `tickets`…). */
  endpoint: string;
  /** Where to return once a creation has succeeded. */
  listUrl: string;
  guildId: string;
  form: HTMLFormElement;
  /** Empty for a creation. */
  itemId: string;
  /** Builds the request body from the form's state. */
  serialize: () => Record<string, unknown>;

  labels: {
    saved: string;
    confirmDelete: string;
    deleted: string;
  };
}

export function mountResourceEditor(options: ResourceEditorOptions): void {
  const { form, endpoint, guildId, itemId } = options;

  trackDirtyForm({
    form,
    successMessage: options.labels.saved,
    onSave: async () => {
      const body = { ...options.serialize(), guildId };

      if (itemId) {
        await api.put(`/api/${endpoint}/${itemId}?guildId=${guildId}`, body);
        return;
      }

      /*
       * A creation returns to the list.
       *
       * Staying on a page whose URL still says "new" would create a second copy
       * on the next save, and the save bar would have no way of saying so.
       */
      await api.post(`/api/${endpoint}?guildId=${guildId}`, body);
      window.location.href = options.listUrl;
    },
  });

  const remove = $<HTMLButtonElement>('[data-editor-delete]');
  if (!remove || !itemId) return;

  remove.addEventListener('click', () => {
    void (async () => {
      if (!(await confirmAction(options.labels.confirmDelete))) return;

      setLoading(remove, true);
      try {
        await api.delete(`/api/${endpoint}/${itemId}?guildId=${guildId}`);
        toastSuccess(options.labels.deleted);
        window.location.href = options.listUrl;
      } catch (error) {
        toastError(error);
        setLoading(remove, false);
      }
    })();
  });
}
